// The Jira query builder (S88): quick starts, then the filters a team usually means by "our
// open work" — projects, issue types, status, assignee, labels, the active sprint and an order
// — with the JQL they make shown underneath. Projects, issue types, statuses and labels come
// from the user's Jira through the broker once a token is chosen.
import { Bug, Tag, UserCircle } from "@phosphor-icons/react";
import { Switch } from "rizzui";

import { useT } from "../../../i18n";
import { FieldSet, Segmented } from "../../../ui/Segmented";
import { Hint, SelectField } from "../../../ui/SideDrawer";
import type { Draft } from "../draft";
import { applyTemplate, ASSIGNEES, type JqlQuery, ORDERS, STATUS_FILTERS, type TemplateKey } from "../jql";
import { ChoicesField } from "./ChoicesField";
import { JqlBox } from "./JqlBox";
import { LabelsField } from "./LabelsField";
import { ProjectField } from "./ProjectField";
import { useProjectFields } from "./useJira";

const QUICK_STARTS: { key: TemplateKey; Icon: typeof Bug }[] = [
  { key: "myOpenWork", Icon: UserCircle },
  { key: "unassignedBugs", Icon: Bug },
  { key: "labelledForAgents", Icon: Tag },
];

function QuickStarts({ onPick }: { onPick: (key: TemplateKey) => void }) {
  const t = useT().tasks.create.jira;
  return (
    <FieldSet legend={t.quickStarts}>
      <div className="grid gap-2.5 sm:grid-cols-3">
        {QUICK_STARTS.map(({ key, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => onPick(key)}
            className="flex items-start gap-2.5 rounded-xl border border-muted p-3 text-start transition-colors hover:border-primary hover:bg-primary-lighter/30"
          >
            <Icon weight="duotone" className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
            <span className="min-w-0">
              <span className="block text-[13px] font-semibold text-gray-900">{t.templates[key].title}</span>
              <span className="block text-xs leading-snug text-gray-500">{t.templates[key].body}</span>
            </span>
          </button>
        ))}
      </div>
    </FieldSet>
  );
}

/** Not part of the JQL: the poller lists the sub-tasks with a second query (S127). */
function SubtasksSwitch({ jira, onChange }: { jira: Draft["jira"]; onChange: (next: Draft["jira"]) => void }) {
  const t = useT().tasks.create.jira;
  return (
    <div>
      <Switch role="switch" label={t.includeSubtasks} checked={jira.includeSubtasks} onChange={(e) => onChange({ ...jira, includeSubtasks: e.target.checked })} />
      <Hint>{t.includeSubtasksHint}</Hint>
    </div>
  );
}

export function JiraBuilder({ jira, credentialId, onChange }: { jira: Draft["jira"]; credentialId: string; onChange: (next: Draft["jira"]) => void }) {
  const t = useT().tasks.create.jira;
  const q = jira.query;
  const set = (patch: Partial<JqlQuery>) => onChange({ ...jira, query: { ...q, ...patch } });
  const fields = useProjectFields(credentialId, q.projects);
  if (jira.raw) {
    return (
      <div className="space-y-6">
        <JqlBox jira={jira} onChange={onChange} />
        <SubtasksSwitch jira={jira} onChange={onChange} />
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <QuickStarts onPick={(key) => onChange({ ...jira, query: applyTemplate(q, key) })} />
      <ProjectField credentialId={credentialId} value={q.projects} onChange={(projects) => set({ projects })} />
      {fields.error ? <p className="-mt-3 mb-0 text-[13px] text-red">{t.lookupFailed(fields.error.message)}</p> : null}
      <ChoicesField legend={t.issueTypes} hint={t.issueTypesHint} options={fields.issueTypes} value={q.issueTypes} onChange={(issueTypes) => set({ issueTypes })} />
      <div className="grid gap-6 md:grid-cols-2">
        <FieldSet legend={t.status}>
          <Segmented label={t.status} options={STATUS_FILTERS.map((key) => ({ key, label: t.statusOptions[key] }))} value={q.status} onChange={(status) => set({ status })} />
        </FieldSet>
        <FieldSet legend={t.assignee}>
          <Segmented label={t.assignee} options={ASSIGNEES.map((key) => ({ key, label: t.assigneeOptions[key] }))} value={q.assignee} onChange={(assignee) => set({ assignee })} />
        </FieldSet>
      </div>
      {q.status === "pick" ? (
        <ChoicesField legend={t.statuses} hint={t.statusesHint} options={fields.statuses} value={q.statuses} onChange={(statuses) => set({ statuses })} />
      ) : null}
      <LabelsField credentialId={credentialId} value={q.labels} onChange={(labels) => set({ labels })} />
      <div className="grid items-end gap-6 md:grid-cols-2">
        <SelectField label={t.order} value={q.order} options={ORDERS.map((o) => ({ value: o, label: t.orderOptions[o] }))} onChange={(v) => set({ order: ORDERS.find((o) => o === v) ?? q.order })} />
        <div className="pb-2">
          <Switch label={t.activeSprint} checked={q.activeSprint} onChange={(e) => set({ activeSprint: e.target.checked })} />
        </div>
      </div>
      <SubtasksSwitch jira={jira} onChange={onChange} />
      <JqlBox jira={jira} onChange={onChange} />
    </div>
  );
}
